'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CounterData {
  id: number;
  icon: string;
  value: number;
  targetValue: number;
  label: string;
  format: 'number' | 'percentage';
  hasPlus: boolean;
}

const ImpactCounters = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [counters, setCounters] = useState<CounterData[]>([
  { id: 1, icon: 'ScaleIcon', value: 0, targetValue: 600, label: 'pounds of food distributed', format: 'number', hasPlus: true },
  { id: 2, icon: 'UserGroupIcon', value: 0, targetValue: 60, label: 'families impacted', format: 'number', hasPlus: true },
  { id: 3, icon: 'CodeBracketIcon', value: 0, targetValue: 80, label: 'of students became proficient in basic coding languages', format: 'percentage', hasPlus: false },
  { id: 4, icon: 'DocumentCheckIcon', value: 0, targetValue: 100, label: 'authored at least one functional program', format: 'percentage', hasPlus: false }]
  );
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated || hasAnimated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isHydrated, hasAnimated]);

  const animateCounters = () => {
    const duration = 2500; // 2.5 seconds for dramatic effect
    const steps = 80;
    const stepDuration = duration / steps;

    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      // Easing function for more dramatic effect (ease-out)
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      setCounters((prev) =>
      prev.map((counter) => ({
        ...counter,
        value: Math.floor(counter.targetValue * easedProgress)
      }))
      );

      if (currentStep >= steps) {
        clearInterval(interval);
        setCounters((prev) =>
        prev.map((counter) => ({
          ...counter,
          value: counter.targetValue
        }))
        );
      }
    }, stepDuration);
  };

  const formatNumber = (counter: CounterData): string => {
    const { value, format, hasPlus } = counter;

    if (format === 'percentage') {
      return `${value}%`;
    }

    // Format with commas for thousands
    const formatted = value.toLocaleString();
    return hasPlus ? `${formatted}+` : formatted;
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {counters.map((counter) =>
            <div key={counter.id} className="text-center">
                <div className="flex justify-center mb-4">
                  <Icon name={counter.icon as any} size={48} className="text-white" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  0
                </div>
                <div className="text-sm md:text-base text-white/90">
                  {counter.label}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>);

  }

  return (
    <section ref={sectionRef} className="py-16 bg-[rgba(79,116,74,1)]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {counters.slice(0, 3).map((counter) =>
          <div key={counter.id} className="text-center transform hover:scale-105 transition-transform duration-300">
              <div className="flex justify-center mb-4">
                <Icon name={counter.icon as any} size={48} className="text-white" />
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {formatNumber(counter)}
              </div>
              <div className="text-sm md:text-base text-white/90">
                {counter.label}
              </div>
            </div>
          )}
        </div>
        {/* Centered 4th counter under the 2nd counter (60 families impacted) */}
        <div className="flex justify-center mt-8">
          <div className="text-center transform hover:scale-105 transition-transform duration-300 max-w-sm">
            <div className="flex justify-center mb-4">
              <Icon name={counters[3].icon as any} size={48} className="text-white" />
            </div>
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">
              {formatNumber(counters[3])}
            </div>
            <div className="text-sm md:text-base text-white/90">
              {counters[3].label}
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default ImpactCounters;